<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import FileUpload from 'vue-upload-component';

import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useFileUpload } from 'dashboard/composables/useFileUpload';
import { ALLOWED_FILE_TYPES } from 'shared/constants/messages';
import { MESSAGE_MAX_LENGTH } from 'shared/helpers/MessageTypeHelper';

import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AttachmentPreviews from 'dashboard/components-next/NewConversation/components/AttachmentPreviews.vue';
import DropdownContainer from 'next/dropdown-menu/base/DropdownContainer.vue';
import DropdownBody from 'next/dropdown-menu/base/DropdownBody.vue';
import DropdownSection from 'next/dropdown-menu/base/DropdownSection.vue';
import DropdownItem from 'next/dropdown-menu/base/DropdownItem.vue';
import WhatsappTemplates from 'dashboard/components/widgets/conversation/WhatsappTemplates/Modal.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import ScheduleDateShortcuts from './ScheduleDateShortcuts.vue';
import RecurrenceDropdown from './RecurrenceDropdown.vue';
import RecurrenceCustomModal from './RecurrenceCustomModal.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: [Number, String],
    default: null,
  },
  scheduledMessage: {
    type: Object,
    default: null,
  },
  initialContent: {
    type: String,
    default: '',
  },
  initialAttachment: {
    type: Object,
    default: null,
  },
  initialRecurring: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:show', 'close', 'scheduledMessageCreated']);

const { t } = useI18n();
const store = useStore();
const alert = useAlert;

const inboxGetter = useMapGetter('inboxes/getInbox');
const uiFlags = useMapGetter('scheduledMessages/getUIFlags');

const isEditing = computed(() => !!props.scheduledMessage?.id);
const isEditingRecurring = computed(
  () =>
    isEditing.value &&
    String(props.scheduledMessage?.id).startsWith('recurring-')
);
const isCreating = computed(() => uiFlags.value.isCreating);
const isUpdating = computed(() => uiFlags.value.isUpdating);
const isSubmitting = computed(() => isCreating.value || isUpdating.value);
const currentInbox = computed(() => inboxGetter.value(props.inboxId));

const whatsAppTemplates = computed(() => {
  return store.getters['inboxes/getWhatsAppTemplates'](props.inboxId) || [];
});

const showWhatsappTemplates = computed(() => {
  return whatsAppTemplates.value.length > 0;
});

const messageContent = ref('');
const scheduledDateTime = ref(null);
const attachments = ref([]);
const existingAttachment = ref(null);
const templateParams = ref(null);
const showConfirmClose = ref(false);
const showWhatsAppTemplatesModal = ref(false);
const contentError = ref(false);
const contentLengthError = ref(false);
const dateTimeError = ref('');
const recurrenceRule = ref(null);
const showRecurrenceCustomModal = ref(false);
const holdOnReply = ref(false);
const isRecurringMode = ref(false);

// Computed to convert Date <-> datetime-local string format
const datetimeLocalValue = computed({
  get() {
    if (!scheduledDateTime.value) return '';
    const d = scheduledDateTime.value;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  },
  set(val) {
    if (!val) {
      scheduledDateTime.value = null;
      return;
    }
    scheduledDateTime.value = new Date(val);
  },
});

// Original values for change detection
const originalContent = ref('');
const originalScheduledAt = ref(null);
const originalHasAttachment = ref(false);
const originalHoldOnReply = ref(false);

// NOTE: Local ref to control modal visibility, prevents auto-close when unsaved changes exist
const localShowModal = ref(props.show);

const removedExistingAttachment = ref(false);

const resetForm = () => {
  messageContent.value = '';
  scheduledDateTime.value = null;
  attachments.value = [];
  existingAttachment.value = null;
  removedExistingAttachment.value = false;
  templateParams.value = null;
  contentError.value = false;
  dateTimeError.value = '';
  recurrenceRule.value = null;
  holdOnReply.value = false;
  isRecurringMode.value = false;
  // Reset original values
  originalContent.value = '';
  originalScheduledAt.value = null;
  originalHasAttachment.value = false;
  originalHoldOnReply.value = false;
};

const setFormFromMessage = scheduledMessage => {
  if (!scheduledMessage) {
    resetForm();
    isRecurringMode.value = props.initialRecurring;
    return;
  }

  messageContent.value = scheduledMessage.content || '';
  templateParams.value = scheduledMessage.template_params || null;
  existingAttachment.value = scheduledMessage.attachment || null;
  attachments.value = [];
  recurrenceRule.value = scheduledMessage.recurrence_rule || null;
  holdOnReply.value = scheduledMessage.hold_on_reply || false;
  isRecurringMode.value = isEditingRecurring.value;

  if (scheduledMessage.scheduled_at) {
    const dateValue = new Date(scheduledMessage.scheduled_at * 1000);
    dateValue.setSeconds(0, 0);
    scheduledDateTime.value = dateValue;
  } else {
    scheduledDateTime.value = null;
  }

  // Store original values for change detection
  originalContent.value = messageContent.value?.trim() || '';
  originalScheduledAt.value = scheduledDateTime.value
    ? new Date(scheduledDateTime.value)
    : null;
  originalHasAttachment.value = !!existingAttachment.value;
  originalHoldOnReply.value = holdOnReply.value;
};

const validateContent = () => {
  contentError.value = !messageContent.value?.trim();
  contentLengthError.value = messageContent.value?.length > MESSAGE_MAX_LENGTH;
  return !contentError.value && !contentLengthError.value;
};

const validateDateTime = () => {
  if (!scheduledDateTime.value) {
    dateTimeError.value = t('SCHEDULED_MESSAGES.ERRORS.DATE_REQUIRED');
    return false;
  }

  const now = new Date();
  now.setSeconds(0, 0);

  if (scheduledDateTime.value < now) {
    dateTimeError.value = t('SCHEDULED_MESSAGES.ERRORS.DATE_IN_PAST');
    return false;
  }

  dateTimeError.value = '';
  return true;
};

const hasUnsavedChanges = computed(() => {
  if (isEditing.value) {
    return (
      messageContent.value?.trim() !== originalContent.value ||
      (scheduledDateTime.value
        ? scheduledDateTime.value.getTime() !== originalScheduledAt.value?.getTime()
        : originalScheduledAt.value !== null) ||
      !!existingAttachment.value !== originalHasAttachment.value ||
      holdOnReply.value !== originalHoldOnReply.value ||
      removedExistingAttachment.value
    );
  }
  return (
    messageContent.value?.trim() ||
    scheduledDateTime.value ||
    attachments.value.length ||
    existingAttachment.value
  );
});

const handleFileUpload = file => {
  const allowedTypes = ALLOWED_FILE_TYPES.join(',');
  if (!allowedTypes.includes(file.type)) {
    alert(t('SCHEDULED_MESSAGES.ERRORS.INVALID_FILE_TYPE'));
    return;
  }

  if (file.size > 100 * 1024 * 1024) {
    alert(t('SCHEDULED_MESSAGES.ERRORS.FILE_TOO_LARGE'));
    return;
  }

  attachments.value = [file];
};

const removeAttachment = () => {
  attachments.value = [];
};

const removeExistingAttachment = () => {
  removedExistingAttachment.value = true;
  existingAttachment.value = null;
};

const handleTemplateSelect = template => {
  messageContent.value = template.content;
  templateParams.value = template.template_params;
};

const handleSubmit = async () => {
  if (!validateContent() || !validateDateTime()) {
    return;
  }

  try {
    const payload = {
      content: messageContent.value,
      scheduledAt: scheduledDateTime.value?.toISOString(),
      templateParams: templateParams.value,
      holdOnReply: holdOnReply.value,
    };

    if (attachments.value.length > 0) {
      payload.attachment = attachments.value[0];
    }

    if (removedExistingAttachment.value) {
      payload.removeAttachment = true;
    }

    const hasRecurrence = isRecurringMode.value;

    if (hasRecurrence && recurrenceRule.value) {
      payload.recurrenceRule = recurrenceRule.value;
    }

    if (isEditing.value) {
      if (isEditingRecurring.value) {
        const recurringId = props.scheduledMessage.id.replace('recurring-', '');
        await store.dispatch('recurringScheduledMessages/update', {
          conversationId: props.conversationId,
          recurringScheduledMessageId: recurringId,
          payload,
        });
      } else {
        await store.dispatch('scheduledMessages/update', {
          conversationId: props.conversationId,
          scheduledMessageId: props.scheduledMessage.id,
          payload,
        });
      }
    } else if (isRecurringMode.value) {
      // Create recurring message (which creates the first occurrence)
      await store.dispatch('recurringScheduledMessages/create', {
        conversationId: props.conversationId,
        payload,
      });
    } else {
      await store.dispatch('scheduledMessages/create', {
        conversationId: props.conversationId,
        payload,
      });
    }

    emit('scheduledMessageCreated');
    // Reset form before closing to skip unsaved changes check
    resetForm();
    emit('update:show', false);
    localShowModal.value = false;
  } catch (error) {
    console.error('Failed to save scheduled message:', error);
    alert(t('SCHEDULED_MESSAGES.ERRORS.SAVE_FAILED'));
  }
};

const closeModal = () => {
  if (hasUnsavedChanges.value) {
    showConfirmClose.value = true;
  } else {
    emit('update:show', false);
    localShowModal.value = false;
    resetForm();
  }
};

const confirmClose = () => {
  showConfirmClose.value = false;
  emit('update:show', false);
  localShowModal.value = false;
  resetForm();
};

watch(
  () => props.show,
  show => {
    localShowModal.value = show;
    if (show) {
      setFormFromMessage(props.scheduledMessage);
    } else if (!showConfirmClose.value) {
      resetForm();
    }
  },
  { immediate: true }
);

watch(localShowModal, val => {
  if (!val && props.show) {
    emit('update:show', false);
  }
});
</script>

<template>
  <Teleport to="body">
    <div v-if="localShowModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex min-h-full items-center justify-center p-4">
        <!-- Backdrop -->
        <div
          class="fixed inset-0 bg-black/50 transition-opacity"
          @click="closeModal"
          aria-hidden="true"
        />

        <!-- Modal -->
        <div class="relative w-full max-w-2xl bg-white rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.4),0_0_0_1px_rgba(255,255,255,0.05)] dark:bg-[#252535] border border-black/5 dark:border-white/10">
          <!-- Header -->
          <div class="flex items-center justify-between border-b border-n-divider px-6 py-4 dark:border-n-divider">
            <h2 class="text-lg font-semibold text-n-text dark:text-n-text-white">
              {{ isEditing ? t('SCHEDULED_MESSAGES.EDIT_TITLE') : t('SCHEDULED_MESSAGES.NEW_TITLE') }}
            </h2>
            <button
              type="button"
              class="text-n-text-light hover:text-n-text dark:hover:text-n-text-white transition-colors"
              @click="closeModal"
              aria-label="Close"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Content -->
          <div class="p-6 space-y-6">
            <!-- Message Content -->
            <div>
              <label class="block text-sm font-medium text-n-text dark:text-n-text-white mb-2">
                {{ t('SCHEDULED_MESSAGES.CONTENT') }}
              </label>
              <WootMessageEditor
                v-model="messageContent"
                :placeholder="t('SCHEDULED_MESSAGES.CONTENT_PLACEHOLDER')"
                :show-emoji-picker="true"
                :show-attachments="false"
                :max-length="MESSAGE_MAX_LENGTH"
                class="w-full"
              />
              <div v-if="contentError" class="mt-1 text-sm text-red-500">
                {{ t('SCHEDULED_MESSAGES.ERRORS.CONTENT_REQUIRED') }}
              </div>
              <div v-if="contentLengthError" class="mt-1 text-sm text-red-500">
                {{ t('SCHEDULED_MESSAGES.ERRORS.CONTENT_TOO_LONG', { max: MESSAGE_MAX_LENGTH }) }}
              </div>
            </div>

            <!-- Attachments -->
            <div v-if="attachments.length || existingAttachment" class="space-y-2">
              <AttachmentPreviews
                :attachments="attachments"
                :existing-attachment="existingAttachment"
                @remove="removeAttachment"
                @remove-existing="removeExistingAttachment"
              />
            </div>

            <div v-if="!attachments.length && !existingAttachment">
              <FileUpload
                class="w-full"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.gif,.mp4,.mp3,.wav"
                @input="handleFileUpload"
              >
                <div class="border-2 border-dashed border-n-divider rounded-lg p-8 text-center dark:border-n-divider">
                  <svg class="mx-auto h-12 w-12 text-n-text-light dark:text-n-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p class="mt-2 text-sm text-n-text dark:text-n-text-white">
                    {{ t('SCHEDULED_MESSAGES.DRAG_DROP_FILE') }}
                  </p>
                  <p class="text-xs text-n-text-light dark:text-n-text-muted">
                    {{ t('SCHEDULED_MESSAGES.FILE_TYPES') }}
                  </p>
                </div>
              </FileUpload>
            </div>

            <!-- Date & Time -->
            <div>
              <label class="block text-sm font-medium text-n-text dark:text-n-text-white mb-2">
                {{ t('SCHEDULED_MESSAGES.SCHEDULE_AT') }}
              </label>
              <div class="flex flex-col sm:flex-row gap-4">
                <input
                  type="datetime-local"
                  v-model="datetimeLocalValue"
                  class="flex-1 px-3 py-2 border border-n-divider rounded-lg bg-white dark:bg-n-surface-1 text-n-text dark:text-n-text-white focus:ring-2 focus:ring-n-primary-500 focus:border-transparent"
                  :class="{ 'border-red-500': dateTimeError }"
                />
                <ScheduleDateShortcuts :selected-datetime="scheduledDateTime" @select="val => scheduledDateTime = val" />
              </div>
              <div v-if="dateTimeError" class="mt-1 text-sm text-red-500">{{ dateTimeError }}</div>
            </div>

            <!-- Recurrence Block (Switch + Options grouped) -->
            <div>
              <!-- Switch -->
              <div v-if="!isEditing" class="flex items-center gap-3 p-3 rounded-lg border transition-colors"
                :class="isRecurringMode
                  ? 'bg-n-primary-50 border-n-primary-300 dark:bg-n-primary-900/20 dark:border-n-primary-700'
                  : 'bg-n-surface-1 border-n-divider dark:bg-white/5 dark:border-white/10'"
              >
                <Switch v-model="isRecurringMode" />
                <div>
                  <p class="text-sm font-medium text-n-text dark:text-n-text-white">
                    {{ t('SCHEDULED_MESSAGES.RECURRENCE_SWITCH') }}
                  </p>
                  <p class="text-xs text-n-text-light dark:text-n-text-muted">
                    {{ t('SCHEDULED_MESSAGES.RECURRENCE_SWITCH_DESCRIPTION') }}
                  </p>
                </div>
              </div>

              <!-- Recurrence Options (visible when switch is ON or editing recurring) -->
              <div v-if="isRecurringMode" class="mt-3 pl-4 border-l-2 border-n-primary-300 dark:border-n-primary-700">
                <RecurrenceDropdown
                  v-model="recurrenceRule"
                  @custom="showRecurrenceCustomModal = true"
                />
              </div>
            </div>

            <!-- Hold on Reply -->
            <div v-if="!isEditingRecurring" class="flex items-center gap-3">
              <Switch
                v-model="holdOnReply"
                :label="t('SCHEDULED_MESSAGES.HOLD_ON_REPLY')"
                :description="t('SCHEDULED_MESSAGES.HOLD_ON_REPLY_DESCRIPTION')"
              />
            </div>

            <!-- WhatsApp Templates -->
            <div v-if="showWhatsappTemplates" class="space-y-2">
              <button
                type="button"
                class="text-sm text-n-primary-600 hover:text-n-primary-700 dark:text-n-primary-400"
                @click="showWhatsAppTemplatesModal = true"
              >
                {{ t('SCHEDULED_MESSAGES.USE_WHATSAPP_TEMPLATE') }}
              </button>
            </div>
          </div>

          <!-- Footer -->
          <div class="flex items-center justify-end gap-3 border-t border-n-divider px-6 py-4 dark:border-n-divider">
            <button
              type="button"
              class="px-4 py-2 text-sm font-medium text-n-text dark:text-n-text-white border border-n-divider rounded-lg hover:bg-n-surface-1 dark:hover:bg-n-surface-3 transition-colors"
              @click="closeModal"
              :disabled="isSubmitting"
            >
              {{ t('SCHEDULED_MESSAGES.DISCARD') }}
            </button>
            <NextButton
              :loading="isSubmitting"
              :disabled="isSubmitting"
              @click="handleSubmit"
            >
              {{ isEditing ? t('SCHEDULED_MESSAGES.SAVE') : t('SCHEDULED_MESSAGES.SCHEDULE') }}
            </NextButton>
          </div>
        </div>
      </div>

      <!-- Confirm Close Modal -->
      <div v-if="showConfirmClose" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4">
          <div class="fixed inset-0 bg-black/50" @click="showConfirmClose = false" />
          <div class="relative w-full max-w-md bg-white rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.4)] p-6 dark:bg-[#252535] border border-black/5 dark:border-white/10">
            <h3 class="text-lg font-semibold text-n-text dark:text-n-text-white mb-2">
              {{ t('SCHEDULED_MESSAGES.CONFIRM_CLOSE_TITLE') }}
            </h3>
            <p class="text-n-text dark:text-n-text-white mb-6">
              {{ t('SCHEDULED_MESSAGES.CONFIRM_CLOSE_MESSAGE') }}
            </p>
            <div class="flex justify-end gap-3">
              <button
                type="button"
                class="px-4 py-2 text-sm font-medium text-n-text dark:text-n-text-white border border-n-divider rounded-lg hover:bg-n-surface-1 dark:hover:bg-n-surface-3"
                @click="showConfirmClose = false"
              >
                {{ t('SCHEDULED_MESSAGES.CONTINUE_EDITING') }}
              </button>
              <button
                type="button"
                class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                @click="confirmClose"
              >
                {{ t('SCHEDULED_MESSAGES.DISCARD') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- WhatsApp Templates Modal -->
      <WhatsAppTemplates
        v-model:show="showWhatsAppTemplatesModal"
        :inbox-id="props.inboxId"
        @select="handleTemplateSelect"
      />

      <!-- Recurrence Custom Modal -->
      <RecurrenceCustomModal
        v-model:show="showRecurrenceCustomModal"
        v-model:recurrence-rule="recurrenceRule"
      />
    </div>
  </Teleport>
</template>